import { ActionIcon, Button, ButtonProps, Tooltip } from "@mantine/core"
import { ReactNode } from "react"
import { ReasonTooltip } from "../ui"
import type { DeliveryActionState } from "./delivery-actions"
import classes from "./action-controls.module.css"

type RowActionProps = Readonly<{
  label: string
  icon: ReactNode
  state?: Pick<DeliveryActionState, "allowed" | "reason">
  onClick: () => void
  loading?: boolean
}>

/**
 * Row action in the first «Үйлдэл» column: grey subtle ActionIcon. Unavailable = aria-disabled with
 * the reason in a tooltip, never hidden (DeHUB UI standard, canvas Ord_Actions).
 */
export function DeliveryRowAction({ label, icon, state, onClick, loading }: RowActionProps) {
  const button = (
    <ActionIcon
      variant="light"
      color="gray"
      size={32}
      radius="md"
      className={classes.rowAction}
      aria-label={label}
      loading={loading}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      {icon}
    </ActionIcon>
  )
  if (state && !state.allowed) {
    return <ReasonTooltip reason={`${label}. ${state.reason ?? ""}`}>{button}</ReasonTooltip>
  }
  return (
    <Tooltip label={label} position="bottom" withArrow openDelay={150}>
      {button}
    </Tooltip>
  )
}

type ActionButtonProps = Readonly<
  {
    state: Pick<DeliveryActionState, "allowed" | "reason">
    /** Extra non-permission reason (missing selection, form invalid). */
    disabledReason?: ReactNode
    children: ReactNode
    onClick?: () => void
    type?: "button" | "submit"
  } & Omit<ButtonProps, "children">
>

/** Button driven by a `useDeliveryActions().stateOf/permissionState` result. */
export function DeliveryActionButton({ state, disabledReason, children, ...buttonProps }: ActionButtonProps) {
  const reason = !state.allowed ? state.reason : disabledReason
  return (
    <ReasonTooltip reason={reason || undefined}>
      <Button {...buttonProps}>{children}</Button>
    </ReasonTooltip>
  )
}
